/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { CSV_EXPORT_PATH, toFilenameSlug, withApiQuery } from "../../../utils/reportExport";

/**
 * The two documents the Daily Logbook exports, each in either format:
 *
 * - Document A (group): the day across every restaurant the current filters resolve to.
 * - Document B (restaurant): one restaurant broken out by revenue center.
 *
 * PDF and CSV are built from the same server-side document builder, so the two formats can
 * never disagree about a number. Only the URLs differ, which is all this module holds — the
 * download machinery itself is shared with the other report exports in utils/reportExport.
 */
export {
  DEFAULT_EXPORT_FORMAT,
  EXPORT_ERROR_MESSAGE,
  EXPORT_FORMATS,
  downloadExport,
  filenameFromDisposition,
  toFilenameSlug,
  triggerBrowserDownload,
} from "../../../utils/reportExport";

const LOGBOOK_PATH = "/v1/admin/daily_logbooks";

export const buildGroupExportUrl = ({ format, businessDate, scope = {} }) =>
  format === "csv"
    ? withApiQuery(CSV_EXPORT_PATH, {
        report: "daily_logbook",
        business_date: businessDate,
        ...scope,
      })
    : withApiQuery(`${LOGBOOK_PATH}/export`, { business_date: businessDate, ...scope });

export const buildRestaurantExportUrl = ({ format, businessDate, restaurantId }) =>
  format === "csv"
    ? withApiQuery(CSV_EXPORT_PATH, {
        report: "daily_logbook_restaurant",
        restaurant_id: restaurantId,
        business_date: businessDate,
      })
    : withApiQuery(`${LOGBOOK_PATH}/${restaurantId}/export`, { business_date: businessDate });

/**
 * Fallback name, used only when a response carries no Content-Disposition. The restaurant
 * documents and the group CSV name themselves this way; the group PDF names itself after its
 * masthead title, which the client does not have.
 */
export const exportFilename = ({ format, businessDate, subject }) => {
  const name = toFilenameSlug(subject) || "group";
  const date = businessDate ? `-${businessDate}` : "";

  return `daily-logbook-${name}${date}.${format}`;
};
